import { supabase } from '@/core/database/supabase';
import type { 
  ApprovalRequest, 
  ApprovalStep, 
  ApprovalComment,
  ApprovalFilters 
} from '../types/approvals.types';

export const ApprovalsService = {
  /**
   * Danh sách yêu cầu phê duyệt dựa trên filter
   */
  async getApprovals(companyId: string, userId: string, filters: ApprovalFilters): Promise<ApprovalRequest[]> {
    let query = supabase
      .from('approvals')
      .select(`
        *,
        requester:user_profiles!requested_by(id, full_name, avatar_url:avatar_path),
        project:projects(id, name)
      `)
      .eq('company_id', companyId);

    // Xử lý loại danh sách
    if (filters.type === 'pending') {
      // Logic lấy pending: là người duyệt bước hiện tại
      // Giả sử có bảng steps hoặc field in_review_by
      query = query.eq('status_code', 'pending');
    } else if (filters.type === 'my-requests') {
      query = query.eq('requested_by', userId);
    }

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status);
    }

    if (filters.module) {
      query = query.eq('module_code', filters.module);
    }

    if (filters.search) {
      query = query.or(`request_title.ilike.%${filters.search}%,request_code.ilike.%${filters.search}%`);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data as ApprovalRequest[];
  },

  /**
   * Chi tiết yêu cầu phê duyệt
   */
  async getApprovalDetail(id: string): Promise<{ 
    request: ApprovalRequest, 
    steps: ApprovalStep[], 
    comments: ApprovalComment[] 
  }> {
    const [requestRes, stepsRes, commentsRes] = await Promise.all([
      supabase.from('approvals').select('*, requester:user_profiles!requested_by(id, full_name, avatar_url:avatar_path), project:projects(id, name)').eq('id', id).single(),
      supabase.from('approval_steps').select('*').eq('approval_id', id).order('step_order', { ascending: true }),
      supabase.from('approval_comments').select('*, user:profiles(id, full_name, avatar_url:avatar_path)').eq('approval_id', id).order('created_at', { ascending: true })
    ]);

    if (requestRes.error) throw requestRes.error;
    
    return {
      request: requestRes.data as ApprovalRequest,
      steps: (stepsRes.data || []) as ApprovalStep[],
      comments: (commentsRes.data || []) as ApprovalComment[]
    };
  },

  /**
   * Thực hiện hành động phê duyệt (Approve/Reject/Request Change)
   */
  async performAction(id: string, action: string, userId: string, comment?: string): Promise<void> {
    const statusMap: Record<string, string> = {
      approve: 'approved',
      reject: 'rejected',
      request_change: 'request_change'
    };

    const { error } = await supabase.rpc('perform_approval_action', {
      p_approval_id: id,
      p_user_id: userId,
      p_action: action,
      p_comment: comment,
      p_new_status: statusMap[action]
    });

    if (error) throw error;
  }
};
